import { Accordion, Text } from '@msc-b2c/shared-components';
import styles from '../stateroomdetails.module.scss';
import PassengersInfo from './PassengersInfo';
import { IncludedWithBookingProps } from '@msc-b2c/interfaces/types';
import { useState } from 'react';
import {
  getDynamicMediaPath,
  getObsBaseIconPath,
  getObsPlaceholderIconPath,
  replacePlaceholders,
} from '@msc-b2c/shared-utils';
import Insurance from './Insurance';
import Hotel from './Hotel';
import Flight from './Flight';
import Transfer from './Transfer';

export default function IncludedWithBooking({
  includedSectionTitle,
  viewDetailsLabel,
  servicesWithPassengers,
  passengerLabel,
  openViewDetailsIncludedIcon,
  closeViewDetailsIncludedIcon,
  insuranceListInfo,
  hotelSectionIcon,
  hotelSectionTitle,
  hotelLabels,
  hotelInfo,
  flightsData,
  transfersData,
  transfersSectionTitle,
  transfersSectionIcon,
  labelsMap,
}: Readonly<IncludedWithBookingProps>) {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className={styles.includedWithBookingContainer}>
      <div className={styles.includedWithBookingTitle}>
        <Text text={includedSectionTitle} />
      </div>
      <div className={styles.includedWithBookingContent}>
        {servicesWithPassengers.map((service) => {
          const imgSrc =
            getDynamicMediaPath(service.code) || `${getObsBaseIconPath()}/${service.code}.svg`;
          const defaultImg = getObsPlaceholderIconPath();

          return (
            <Accordion
              key={service.id}
              question={service.description}
              answer=""
              isOpen={openId === service.id}
              autoClose={false}
              openingIcon={openViewDetailsIncludedIcon}
              closingIcon={closeViewDetailsIncludedIcon}
              multi={true}
              onToggle={() => setOpenId((prev) => (prev === service.id ? null : service.id))}
              viewDetailsLabel={viewDetailsLabel}
              styleOption={styles.includedWithBookingAccordion}
              questionImage={imgSrc}
              placeholderImagePath={defaultImg}
            >
              <>
                <div className={styles.passengerDetailsContainer}>
                  <div
                    className={`${styles.passengerDetailsContent} ${styles.includedWithBookingAccordionActive}`}
                  >
                    {service.passengers.map((p, index) => {
                      const enhancedPassengerLabel = replacePlaceholders(passengerLabel, {
                        passengerIndex: passengerLabel.length > 1 ? `${index + 1}` : '',
                      });
                      return (
                        <PassengersInfo
                          key={p.id}
                          passengerLabel={enhancedPassengerLabel}
                          passengerName={`${p.name} ${p.surname}`}
                        />
                      );
                    })}
                  </div>
                </div>
                <Text
                  text={service.specialInstructions}
                  styleOption={styles.specialInstructions}
                />
              </>
            </Accordion>
          );
        })}
        {flightsData.map((flight) => {
          return (
            <Flight
              key={flight.code}
              flightId={flight.code ?? ''}
              viewDetailsFlightLabel={viewDetailsLabel}
              openViewDetailsFlightIcon={openViewDetailsIncludedIcon}
              closeViewDetailsFlightIcon={closeViewDetailsIncludedIcon}
              passengers={flight.passengers}
              flightStatusLabel={flight.configLabels.statusLabel}
              flightClassLabel={flight.configLabels.classLabel}
              airlineLabel={flight.configLabels.airlineLabel}
              flightDetailsLabel={flight.configLabels.detailsLabel}
              flightsSectionIcon={flight.configLabels.flightsSectionIcon}
              flightsSectionTitle={flight.configLabels.flightsSectionTitle}
              flightStatusValue={flight.status}
              flightSegments={flight.segments}
            />
          );
        })}
        {/* Render tranfer items if available */}
        {transfersData && transfersData.length > 0 && (
          <Transfer
            transferId={transfersSectionTitle}
            transferSectionTitle={transfersSectionTitle}
            transferSectionIcon={transfersSectionIcon}
            viewDetailsLabel={viewDetailsLabel}
            openViewDetailsIcon={openViewDetailsIncludedIcon}
            closeViewDetailsIcon={closeViewDetailsIncludedIcon}
            transfersData={transfersData}
          />
        )}
        {/* Render insurance items if available */}
        {insuranceListInfo?.map((insurance) => {
          return (
            <Insurance
              key={insurance.code}
              insuranceInfo={insurance}
              viewDetailsInsuranceLabel={viewDetailsLabel}
              openViewDetailsInsuranceIcon={openViewDetailsIncludedIcon}
              closeViewDetailsInsuranceIcon={closeViewDetailsIncludedIcon}
            />
          );
        })}

        {/* API Data */}
        {Array.isArray(hotelInfo) &&
          hotelInfo.length > 0 &&
          hotelInfo.map((hotel) => (
            <Hotel
              labelsMap={labelsMap}
              key={hotel.code}
              hotelSectionIcon={hotelSectionIcon}
              hotelSectionTitle={hotelSectionTitle}
              hotelData={hotel}
              hotelLabels={hotelLabels}
              viewDetailsHotelLabel={viewDetailsLabel}
              openViewDetailsHotelIcon={openViewDetailsIncludedIcon}
              closeViewDetailsHotelIcon={closeViewDetailsIncludedIcon}
            />
          ))}
      </div>
    </div>
  );
}
